import { useState, useRef, useCallback } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { CostTypeSelector } from './CostTypeChip'

/* ─── Role/Item autocomplete ─── */
const RoleAutocomplete = ({ comp, laborRates, equipmentRates, onChange }) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const blurTimer = useRef(null)

  const isLabor = comp.costType === 'labor'
  const isEquipment = comp.costType === 'equipment'

  const currentValue = isLabor
    ? (comp.laborRole || '')
    : isEquipment
      ? (comp.equipmentRole || '')
      : ''

  const [inputVal, setInputVal] = useState(currentValue)

  // Sync inputVal when comp changes externally
  const displayVal = query !== '' ? query : currentValue

  const options = (() => {
    if (!isLabor && !isEquipment) return []
    const needle = (query || currentValue).toLowerCase()
    if (isLabor) {
      return laborRates
        .filter(r => r.role && r.role.toLowerCase().includes(needle))
        .slice(0, 10)
    }
    return equipmentRates
      .filter(r => r.name && r.name.toLowerCase().includes(needle))
      .slice(0, 10)
  })()

  const handleFocus = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current)
    setOpen(true)
  }

  const handleBlur = () => {
    blurTimer.current = setTimeout(() => {
      setOpen(false)
      setQuery('')
    }, 150)
  }

  const handleSelect = (opt) => {
    if (blurTimer.current) clearTimeout(blurTimer.current)
    if (isLabor) {
      onChange({ laborRole: opt.role })
    } else {
      onChange({ equipmentRole: opt.name })
    }
    setQuery('')
    setOpen(false)
  }

  const handleChange = (e) => {
    setQuery(e.target.value)
    setOpen(true)
  }

  if (!isLabor && !isEquipment) {
    return (
      <input
        className="bare"
        placeholder="—"
        style={{ opacity: 0.4, cursor: 'default', width: '100%' }}
        readOnly
        tabIndex={-1}
      />
    )
  }

  return (
    <div className="autocomplete" style={{ minWidth: 130 }}>
      <input
        className="bare"
        value={query !== '' ? query : currentValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={isLabor ? 'Labor role…' : 'Equipment…'}
        style={{ width: '100%' }}
      />
      {open && options.length > 0 && (
        <div className="autocomplete-dropdown">
          {options.map((opt, i) => (
            <div
              key={i}
              className="autocomplete-option"
              onMouseDown={() => handleSelect(opt)}
            >
              <div className="option-label">{isLabor ? opt.role : opt.name}</div>
              {isEquipment && opt.hourlyRate != null && (
                <div className="option-detail">${opt.hourlyRate}/hr</div>
              )}
              {isLabor && opt.hourlyRate != null && (
                <div className="option-detail">${opt.hourlyRate}/hr</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Prod Rate cell ─── */
const ProdRateCell = ({ comp, productionRates, onChange }) => {
  const isAggregate = comp.quantityMode === 'aggregate'

  const matchedRate = (() => {
    if (comp.productionRateId) {
      return productionRates.find(p => p._id === comp.productionRateId) || null
    }
    if (comp.laborRole) {
      return productionRates.find(p => p.role === comp.laborRole) || null
    }
    return null
  })()

  const toggleMode = () => {
    onChange({ quantityMode: isAggregate ? 'service_total' : 'aggregate' })
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 160 }}>
      {isAggregate ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
          <input
            className="bare mono"
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={comp.aggregatePct ?? ''}
            onChange={e => onChange({ aggregatePct: parseFloat(e.target.value) || 0 })}
            placeholder="0"
            style={{ width: 44, textAlign: 'right' }}
          />
          <span style={{ fontSize: 11, color: 'var(--ink-4)', whiteSpace: 'nowrap' }}>% of</span>
          <select
            className="bare"
            value={comp.aggregateOf || 'all'}
            onChange={e => onChange({ aggregateOf: e.target.value })}
            style={{ minWidth: 80 }}
          >
            <option value="all">All</option>
            <option value="labor">Labor</option>
            <option value="equipment">Equipment</option>
            <option value="material">Material</option>
            <option value="subcontract">Subcontract</option>
          </select>
        </div>
      ) : (
        <input
          className="bare mono"
          readOnly
          value={matchedRate ? matchedRate.description || `${matchedRate.baseRate} ${matchedRate.unit}` : '—'}
          style={{ flex: 1, opacity: matchedRate ? 1 : 0.35, cursor: 'default' }}
          tabIndex={-1}
        />
      )}
      <button
        type="button"
        onClick={toggleMode}
        title={isAggregate ? 'Switch to production rate mode' : 'Switch to aggregate % mode'}
        style={{
          border: '1px solid',
          borderColor: isAggregate ? 'var(--subcontract)' : 'var(--line)',
          background: isAggregate ? 'var(--subcontract-soft)' : 'transparent',
          color: isAggregate ? 'var(--subcontract)' : 'var(--ink-4)',
          borderRadius: 'var(--r-sm)',
          fontSize: 10,
          fontFamily: 'var(--font-mono)',
          fontWeight: 700,
          padding: '2px 5px',
          cursor: 'pointer',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          transition: 'all 0.15s',
          flexShrink: 0
        }}
      >
        %
      </button>
    </div>
  )
}

/* ─── Single component row ─── */
const CompRow = ({ comp, index, laborRates, equipmentRates, productionRates, onChange, onDelete }) => {
  const update = useCallback((patch) => onChange(index, patch), [onChange, index])

  return (
    <Draggable draggableId={String(index)} index={index}>
      {(provided, snapshot) => (
        <tr
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={snapshot.isDragging ? 'row-dragging' : ''}
        >
          {/* Drag handle */}
          <td style={{ width: 28, padding: '6px 4px' }}>
            <span className="drag-handle" {...provided.dragHandleProps}>
              {'\u2630'}
            </span>
          </td>

          {/* Cost type */}
          <td style={{ width: 112 }}>
            <CostTypeSelector
              value={comp.costType || 'labor'}
              onChange={(ct) => update({ costType: ct })}
            />
          </td>

          {/* Description */}
          <td style={{ minWidth: 140 }}>
            <input
              className="bare"
              value={comp.description || ''}
              onChange={e => update({ description: e.target.value })}
              placeholder="Description…"
              style={{ width: '100%' }}
            />
          </td>

          {/* Role / Item */}
          <td style={{ minWidth: 140 }}>
            <RoleAutocomplete
              comp={comp}
              laborRates={laborRates}
              equipmentRates={equipmentRates}
              onChange={(patch) => update(patch)}
            />
          </td>

          {/* Prod Rate */}
          <td style={{ minWidth: 170 }}>
            <ProdRateCell
              comp={comp}
              productionRates={productionRates}
              onChange={(patch) => update(patch)}
            />
          </td>

          {/* Minimums */}
          <td style={{ width: 120 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                className="bare mono"
                type="number"
                min={0}
                step={0.25}
                value={comp.minimumHours ?? ''}
                onChange={e => update({ minimumHours: parseFloat(e.target.value) || 0 })}
                placeholder="hrs"
                style={{ width: 50, textAlign: 'right' }}
                title="Minimum hours"
              />
              <input
                className="bare mono"
                type="number"
                min={0}
                step={1}
                value={comp.minimumCost ?? ''}
                onChange={e => update({ minimumCost: parseFloat(e.target.value) || 0 })}
                placeholder="$"
                style={{ width: 56, textAlign: 'right' }}
                title="Minimum cost"
              />
            </div>
          </td>

          {/* Flags */}
          <td style={{ width: 90 }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <span
                className={`flag flag-opt${comp.isOptional ? ' active' : ''}`}
                onClick={() => update({ isOptional: !comp.isOptional })}
                title="Optional component"
              >
                OPT
              </span>
              {comp.costType === 'labor' && (
                <span
                  className={`flag flag-flw${comp.followsLeadHours ? ' active' : ''}`}
                  onClick={() => update({ followsLeadHours: !comp.followsLeadHours })}
                  title="Follows lead hours"
                >
                  FLW
                </span>
              )}
            </div>
          </td>

          {/* Delete */}
          <td style={{ width: 32, padding: '6px 4px' }}>
            <button
              type="button"
              className="row-delete"
              onClick={() => onDelete(index)}
              title="Remove component"
            >
              {'\u00D7'}
            </button>
          </td>
        </tr>
      )}
    </Draggable>
  )
}

/* ═══════════════════════════════════════════════════════════
   ComponentsTab
   ═══════════════════════════════════════════════════════════ */
const ComponentsTab = ({ comps, setComps, laborRates, equipmentRates, productionRates, onConfirm }) => {

  const handleChange = useCallback((index, patch) => {
    setComps(prev => prev.map((c, i) => i === index ? { ...c, ...patch } : c))
  }, [setComps])

  const handleDelete = useCallback((index) => {
    const comp = comps[index]
    const label = comp.description || `Component ${index + 1}`
    onConfirm({
      title: 'Remove Component',
      subtitle: `"${label}"`,
      message: 'This component will be permanently removed from the recipe. This action cannot be undone.',
      actionLabel: 'Remove',
      onConfirm: () => {
        setComps(prev => prev.filter((_, i) => i !== index))
      }
    })
  }, [comps, setComps, onConfirm])

  const handleAdd = useCallback(() => {
    setComps(prev => [
      ...prev,
      {
        costType: 'labor',
        description: '',
        laborRole: '',
        quantityMode: 'service_total',
        defaultQty: 0
      }
    ])
  }, [setComps])

  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return
    const from = result.source.index
    const to = result.destination.index
    if (from === to) return
    const next = Array.from(comps)
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setComps(next)
  }, [comps, setComps])

  return (
    <div>
      {comps.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">{'\u{1F9F1}'}</div>
          <div className="empty-title">No components defined</div>
          <div className="empty-subtitle">Add a labor, equipment, material, or subcontract component to build this recipe.</div>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <table className="comp-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Type</th>
                <th>Description</th>
                <th>Role</th>
                <th>Prod Rate</th>
                <th>Min</th>
                <th>Flags</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <Droppable droppableId="components">
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {comps.map((comp, i) => (
                    <CompRow
                      key={i}
                      comp={comp}
                      index={i}
                      laborRates={laborRates}
                      equipmentRates={equipmentRates}
                      productionRates={productionRates}
                      onChange={handleChange}
                      onDelete={handleDelete}
                    />
                  ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </table>
        </DragDropContext>
      )}

      <div style={{ marginTop: comps.length === 0 ? 16 : 12 }}>
        <button type="button" className="btn btn-ghost" onClick={handleAdd}>
          + Add Component
        </button>
      </div>
    </div>
  )
}

export default ComponentsTab
